import React, { useEffect, useState } from "react";
import {
  Modal,
  Card,
  Row,
  Col,
  Typography,
  Avatar,
  Badge,
  Button,
  Divider,
  Tooltip,
  Statistic,
  Tag,
  Space,
  Table,
  message,
  Image,
  Select,
  Input,
  Switch,
  Popover,
} from "antd";
import {
  UserOutlined,
  EditOutlined,
  EyeOutlined,
  WalletOutlined,
  LoginOutlined,
  CalendarOutlined,
  CreditCardOutlined,
  SafetyOutlined,
  IdcardOutlined,
  PhoneOutlined,
  MailOutlined,
  GlobalOutlined,
  ManOutlined,
  WomanOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { downloadQrAsPng, downloadQrAsPdf, QrDownloadModal } from "./QrCode";
import { DollarSign, Infinity, RotateCcwSquare } from "lucide-react";
import useRequest from "../hooks/useRequest";
import { ShowToast, Severty } from "../helper/toast";

const { Title, Text } = Typography;
const { Option } = Select;

const SurfaceCard = ({ children, bg, border }) => (
  <Card
    bordered={false}
    style={{
      height: "100%",
      borderRadius: 20,
      background: bg,
      border,
      boxShadow: "0 12px 30px rgba(0,0,0,.06)",
    }}
  >
    {children}
  </Card>
);

const InfoItem = ({ icon, label, value }) => (
  <div
    style={{
      display: "flex",
      gap: 12,
      alignItems: "flex-start",
      marginBottom: 14,
    }}
  >
    {icon && (
      <span
        style={{
          color: "#8c8c8c",
          fontSize: 16,
          marginTop: 2,
        }}
      >
        {icon}
      </span>
    )}

    <div>
      <Text type="secondary" strong style={{ fontSize: 15 }}>
        {label}
      </Text>
      <div style={{ fontWeight: 500 }}>{value || "N/A"}</div>
    </div>
  </div>
);

const SectionTitle = ({ icon, title, extra }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", justifyContent: "space-between" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <span style={{ fontSize: 18, color: "#1677ff" }}>{icon}</span>
      <Title level={5} style={{ margin: 0 }}>
        {title}
      </Title>
    </div>
    {extra && <div>{extra}</div>}
  </div>
);

const UserProfileModal = ({
  open,
  onClose,
  onEdit,
  user,
  mapStatusToBoolean,
  onViewWallet,
  ownerType,
}) => {
  const { request } = useRequest();
  const [documentModal, setDocumentModal] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [packages, setPackages] = useState([]);
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [assignLoading, setAssignLoading] = useState(false);
  const [isMonthlyTrial, setIsMonthlyTrial] = useState(false);
  const [monthlyTrialDays, setMonthlyTrialDays] = useState(0);
  const [isTransactionTrial, setIsTransactionTrial] = useState(false);
  const [transactionTrialDays, setTransactionTrialDays] = useState(0);

  // Edit limits states
  const [editLimitsVisible, setEditLimitsVisible] = useState(false);
  const [editLimitsLoading, setEditLimitsLoading] = useState(false);
  const [editDailyLimit, setEditDailyLimit] = useState("");
  const [editMonthlyLimit, setEditMonthlyLimit] = useState("");
  const [editDailyCount, setEditDailyCount] = useState("");

  // QR Code download modal states
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [selectedWalletForQr, setSelectedWalletForQr] = useState(null);

  const userProfile = JSON.parse(localStorage.getItem("user") || "{}");
  const subPerm = userProfile?.role?.module_permissions?.find(p => p.module === "subscription");
  const canAssignSubscription = subPerm?.can_create || false;

  useEffect(() => {
    if (!open) {
      setDocumentModal(false);
      setSelectedDocuments([]);
      setAssignModalVisible(false);
      setSelectedPackageId(null);
      setIsMonthlyTrial(false);
      setMonthlyTrialDays(0);
      setIsTransactionTrial(false);
      setTransactionTrialDays(0);
      setEditLimitsVisible(false);
    }
  }, [open]);

  if (!user) return null;

  const fetchActivePackages = () => {
    request({
      url: "/api/admin/user-subscription",
      method: "GET",
      onSuccess: (res) => {
        const list = res?.data || [];
        setPackages(list.filter(p => p.is_active));
      }
    });
  };

  const handlePackageChange = (val) => {
    setSelectedPackageId(val);
    const pkg = packages.find(p => p.id === val);
    if (pkg) {
      setIsMonthlyTrial(pkg.is_monthly_trial || false);
      setMonthlyTrialDays(pkg.monthly_trial_days || 0);
      setIsTransactionTrial(pkg.is_transaction_trial || false);
      setTransactionTrialDays(pkg.transaction_trial_days || 0);
    }
  };

  const handleAssignSubscription = () => {
    if (!selectedPackageId) {
      ShowToast("Please select a subscription package", Severty.ERROR);
      return;
    }
    setAssignLoading(true);
    request({
      url: "/api/admin/assign-subscription",
      method: "POST",
      data: {
        type: "user",
        id: user.id,
        package_id: selectedPackageId,
        is_monthly_trial: isMonthlyTrial,
        monthly_trial_days: monthlyTrialDays,
        is_transaction_trial: isTransactionTrial,
        transaction_trial_days: transactionTrialDays,
      },
      onSuccess: () => {
        ShowToast("Subscription manually assigned successfully", Severty.SUCCESS);
        setAssignLoading(false);
        setAssignModalVisible(false);
        onClose(); // close profile modal to trigger refresh on list
      },
      onError: () => {
        ShowToast("Failed to assign subscription", Severty.ERROR);
        setAssignLoading(false);
      }
    });
  };

  const handleUpdateLimits = () => {
    setEditLimitsLoading(true);
    request({
      url: "/api/admin/assign-subscription",
      method: "PUT",
      data: {
        type: ownerType === "users" ? "user" : "merchant",
        id: user.id,
        daily_transaction_limit: editDailyLimit,
        monthly_transaction_limit: editMonthlyLimit,
        daily_transaction_count: editDailyCount,
      },
      onSuccess: () => {
        ShowToast("Subscription limits updated successfully", Severty.SUCCESS);
        setEditLimitsLoading(false);
        setEditLimitsVisible(false);
        onClose(); // close profile modal to trigger refresh
      },
      onError: () => {
        ShowToast("Failed to update subscription limits", Severty.ERROR);
        setEditLimitsLoading(false);
      }
    });
  };

  const handleDownloadQr = (wallet) => {
    const qrCodeUrl = wallet?.qrCode;
    if (!qrCodeUrl || qrCodeUrl === "N/A") {
      message.warning("QR code not available for this wallet");
      return;
    }
    setSelectedWalletForQr(wallet);
    setQrModalVisible(true);
  };

  const isActive = mapStatusToBoolean(user.status || user.isActive);
  const walletDetails = user.wallet ?? [];
  const normalizedUser = ownerType === "users" ? user : user?.users;
  let totalBalance = 0;
  
  const normalizedWallets = Array.isArray(walletDetails)
    ? walletDetails
    : walletDetails
      ? [walletDetails]
      : [];

  const walletRows = normalizedWallets.map((wallet, index) => {
    if (wallet?.wallet) {
      wallet = wallet?.wallet;
    }
    totalBalance += wallet?.balance ?? 10;
    return {
      key: index,
      walletId: wallet?.wallet_id ?? wallet?.id ?? "N/A",
      balance: wallet?.balance ?? Number(wallet?.account_balance) ?? "N/A",
      accountId: wallet?.account_id ?? wallet?.account ?? "N/A",
      payId: wallet?.pay_id ?? "N/A",
      qrCode: wallet?.qr_code ?? "N/A",
    }
  });

  const walletColumns = [
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      render: (value) => `NZ$ ${value}`,
    },
    {
      title: "Account ID",
      dataIndex: "accountId",
      key: "accountId",
    },
    {
      title: "Pay ID",
      dataIndex: "payId",
      key: "payId",
    },
    {
      title: "QR Code",
      key: "qrCode",
      align: "center",
      render: (wallet) => (
        <Button
          size="middle"
          type="primary"
          icon={<DownloadOutlined />}
          onClick={() => {
            handleDownloadQr(wallet);
          }}
        >
          Download
        </Button>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (wallet) => (
        <Button
          size="middle"
          type="primary"
          icon={<EyeOutlined />}
          onClick={() => {
            onViewWallet(wallet);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  const financialBankColumns = [
    {
      title: "Bank Name",
      dataIndex: "bank_name",
      key: "bank_name",
      render: (_, record) => {
        const { bank_name, logo } = record;
        return (
          <Space>
            <Image
              src={logo}
              alt={bank_name}
              width={30}
              height={30}
              style={{ objectFit: "contain" }}
              preview={{ mask: "Preview" }}
            />
            <span>{bank_name}</span>
          </Space>
        );
      },
    },
    {
      title: "Account Number",
      dataIndex: "account_number",
      key: "account_number",
    },
    {
      title: "Account Holder Name",
      dataIndex: "account_holder_name",
      key: "account_holder_name",
    },
    {
      title: "Account Type",
      dataIndex: "account_type",
      key: "account_type",
      render: (value) => <Tag color={value === "CHECKING" ? "green" : "red"}>{value}</Tag>,
    },
    {
      title: "Account Status",
      dataIndex: "account_status",
      key: "account_status",
      render: (value) => <Tag color={value === "ACTIVE" ? "green" : "red"}>{value}</Tag>,
    },
  ];

  const kycDocumentColumns = [
    {
      title: "Document Role",
      dataIndex: "doc_role",
      width: 120,
      render: (_, record) => <Text code>{record?.doc_role}</Text>,
    },
    {
      title: "Document Name",
      dataIndex: "doc_step_name",
      width: 180,
      render: (_, record) => <Text>{record?.doc_step_name}</Text>,
    },
    {
      title: "Preview",
      dataIndex: "url",
      width: 150,
      render: (url) => (
        <Image
          src={url}
          alt="document"
          width={60}
          height={60}
          style={{ objectFit: "cover", borderRadius: 6 }}
        />
      ),
    },
  ];

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        width="80vw"
        centered
        destroyOnClose
        zIndex={1000}
        styles={{
          content: {
            padding: 0,
            borderRadius: 24,
            overflow: "hidden",
            background: "#f5f7fb",
          },
        }}
      >
        {/* ===== HERO HEADER ===== */}
        <div
          style={{
            padding: "12px 16px",
            background: "linear-gradient(135deg, #eef3ff 0%, #f9fcff 100%)",
            borderBottom: "1px solid #e4ebf5",
          }}
        >
          <Row justify="space-between" align="middle" className="mb-3" gutter={[16, 16]} >
            <Col>
              <div style={{ display: "flex", gap: 20, flexWrap: "wrap", gap: "10px" }}>
                {(normalizedUser?.profile_img || normalizedUser?.business_logo) ? (
                  <img 
                    src={normalizedUser?.profile_img || normalizedUser?.business_logo} 
                    alt="profile" 
                    style={{ 
                      width: 90, 
                      height: 90, 
                      minWidth: 90,
                      minHeight: 90,
                      borderRadius: "50%", 
                      objectFit: "cover", 
                      objectPosition: "center",
                      border: "4px solid #fff",
                      backgroundColor: "#e6f0ff",
                      display: "block",
                    }} 
                  />
                ) : (
                  <Avatar
                    size={90}
                    icon={<UserOutlined />}
                    style={{
                      background: "#e6f0ff",
                      color: "#2952cc",
                      border: "4px solid #fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                    }}
                  />
                )}
                <div>
                  <Title level={2} style={{ marginBottom: 4 }}>
                    <IdcardOutlined style={{ marginRight: 10, color: "#1677ff" }} />
                    {normalizedUser?.pronoun} {(normalizedUser?.first_name ?? "") + " " + (normalizedUser?.middle_name ?? "") + " " + (normalizedUser?.last_name ?? "")}
                  </Title>
                  <div style={{ marginTop: 8 }}>
                    <Tag color="blue">{normalizedUser?.id}</Tag>
                    <Badge
                      status={isActive ? "success" : "error"}
                      text={isActive ? "Active" : "Inactive"}
                      style={{ marginLeft: 12 }}
                    />
                  </div>
                </div>
              </div>
            </Col>

            {ownerType === "users" && (
              <Col>
                <Button
                  size="large"
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={onEdit}
                >
                  Edit Profile
                </Button>
              </Col>
            )}
          </Row>
        </div>

        {/* ===== STATS STRIP ===== */}
        <div style={{ padding: "12px 16px" }}>
          <Row gutter={[16, 16]}>
            <Col span={24} md={12} lg={8}>
              <SurfaceCard bg="linear-gradient(135deg, #f0f9ff, #e6fffb)" border="1px solid #cceeff">
                <Statistic
                  title="Wallet Balance"
                  prefix={<WalletOutlined style={{ color: "#13c2c2" }} />}
                  value={`NZ$ ${totalBalance}`}
                />
              </SurfaceCard>
            </Col>

            <Col span={24} md={12} lg={8}>
              <SurfaceCard bg="linear-gradient(135deg, #f0f9ff, #e6fffb)" border="1px solid #cceeff">
                <Statistic
                  title="Last Login"
                  prefix={<LoginOutlined style={{ color: "#722ed1" }} />}
                  value={normalizedUser?.last_login ? moment(normalizedUser.last_login).fromNow() : "Never"}
                />
              </SurfaceCard>
            </Col>

            <Col span={24} md={12} lg={8}>
              <SurfaceCard bg="linear-gradient(135deg, #f0f9ff, #e6fffb)" border="1px solid #cceeff">
                <Statistic
                  title="Member Since"
                  prefix={<CalendarOutlined style={{ color: "#fa8c16" }} />}
                  value={moment(normalizedUser?.join_date).format("DD MMM YYYY")}
                />
              </SurfaceCard>
            </Col>
          </Row>
        </div>

        {/* ===== MAIN CONTENT ===== */}
        <div style={{ padding: "0 16px 16px" }}>
          <Row gutter={[24, 24]}>
            {/* PERSONAL */}
            <Col span={24} md={12}>
              <SurfaceCard bg="linear-gradient(135deg, #f0f9ff, #e6fffb)" border="1px solid #cceeff">
                <SectionTitle 
                  icon={<UserOutlined />} 
                  title="Personal Details" 
                  className="popover-section"
                  extra={
                    <Popover
                      overlayInnerStyle={{ 
                        borderRadius: 12, 
                        padding: "16px 20px", 
                        backgroundColor: "#d1faffe7", 
                        boxShadow: "0 12px 36px rgba(94, 94, 94, 0.72), 0 4px 12px rgba(128, 128, 128, 0.53)",
                        backdropFilter: "blur(8px)"
                      }}
                     
                      content={
                        <div style={{ width: 320, marginTop: 12 }}>
                          {[
                            { q: "Education Qualification", a: normalizedUser?.qualification },
                            { q: "Occupation", a: normalizedUser?.occupation },
                            { q: "Employment Status", a: normalizedUser?.employment_status },
                            { q: "Source of Fund", a: normalizedUser?.source_of_fund },
                            { q: "Monthly Transaction Volume", a: normalizedUser?.monthly_transaction },
                            { q: "Transactions per month", a: normalizedUser?.transaction_per_month },
                            { q: "How will you use DuePay?", a: normalizedUser?.duepay_use },
                          ].map((item, idx, arr) => (
                            <div 
                              key={idx} 
                              style={{ 
                                marginBottom: idx === arr.length - 1 ? 0 : 12, 
                                paddingBottom: idx === arr.length - 1 ? 0 : 12, 
                                borderBottom: idx === arr.length - 1 ? "none" : "1px solid rgba(0,0,0,0.06)" 
                              }}
                            >
                              <Text style={{ display: "block", fontSize: 14, fontWeight: 600, color: "#1f2937", marginBottom: 4 }}>
                                {item.q}
                              </Text>
                              <Text style={{ fontSize: 13, color: "#4b5563" }}>
                                {item.a || "N/A"}
                              </Text>
                            </div>
                          ))}
                        </div>
                      }
                      title={
                        <div style={{ color: "#1677ff", fontSize: 16, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
                          <InfoCircleOutlined />
                          Additional Details
                        </div>
                      }
                      trigger="click"
                      placement="bottomRight"
                     
                    >
                      <Button 
                        type="text" 
                        shape="circle" 
                        icon={<InfoCircleOutlined style={{ color: "#1677ff", fontSize: 18 }} />} 
                        style={{ background: "#e6f0ff", border: "1px solid #b3d8ff" }}
                        title="View Additional Details"
                      />
                    </Popover>
                  }
                />
                <Divider />

                <InfoItem
                  icon={<IdcardOutlined />}
                  label="Full Name"
                  value={(normalizedUser?.first_name ?? "") + " " + (normalizedUser?.middle_name ?? "") + " " + (normalizedUser?.last_name ?? "")}
                />

                <InfoItem
                  icon={normalizedUser?.gender === "MALE" ? <ManOutlined /> : <WomanOutlined />}
                  label="Gender"
                  value={normalizedUser?.gender}
                />

                <InfoItem
                  icon={<CalendarOutlined />}
                  label="Date of Birth"
                  value={normalizedUser?.date_of_birth ? moment(normalizedUser.date_of_birth).format("DD MMM YYYY") : null}
                />
              </SurfaceCard>
            </Col>

            {/* ACCOUNT */}
            <Col span={24} md={12} >
              <SurfaceCard bg="linear-gradient(135deg, #f0f9ff, #e6fffb)" border="1px solid #cceeff">
                <SectionTitle icon={<SafetyOutlined />} title="Account Information" />
                <Divider />

                <InfoItem
                  icon={<MailOutlined />}
                  label="Email"
                  value={normalizedUser?.email}
                />

                <InfoItem
                  icon={<PhoneOutlined />}
                  label="Mobile Number"
                  value={(normalizedUser?.country_code ?? "+64") + " " + normalizedUser?.mobile}
                />

                <InfoItem
                  icon={<GlobalOutlined />}
                  label="Primary Address"
                  value={
                    normalizedUser?.address && normalizedUser.address.length > 0
                      ? `${normalizedUser.address[0].street}, ${normalizedUser.address[0].city}, ${normalizedUser.address[0].district} ${normalizedUser.address[0].postal_code}`
                      : "N/A"
                  }
                />
              </SurfaceCard>
            </Col>

            <Col span={24}>
              <SurfaceCard bg="linear-gradient(135deg, #f0f9ff, #e6fffb)" border="1px solid #cceeff">
                <SectionTitle icon={<UserOutlined />} title="AML and PEP Screening" />
                <Divider />

                <Row gutter={24}>
                  <Col span={12}>
                    <InfoItem label="KYC Status" value={normalizedUser?.kyc?.status ?? "N/A"} />
                    <InfoItem label="PEP(Politically Exposed Person)" value={<Text>{normalizedUser?.kyc?.pep ? "Yes" : "No"}</Text>} />
                  </Col>

                  <Col span={12}>
                    <InfoItem
                      label="Last KYC Date"
                      value={normalizedUser?.kyc?.approved_at ? moment(normalizedUser.kyc.approved_at).format("DD MMM YYYY") : "N/A"}
                    />
                    <InfoItem label="GBG Reference" value={normalizedUser?.kyc?.gbg_reference ?? "N/A"} />
                  </Col>
                </Row>
              </SurfaceCard>
            </Col>

            {normalizedUser?.kyc?.document_submitted && (
              <Col span={24}>
                <SurfaceCard bg="linear-gradient(135deg, #f0f9ff, #e6fffb)" border="1px solid #cceeff">
                  <SectionTitle icon={<UserOutlined />} title="Identification Details" />
                  <Divider />

                  <Row gutter={24}>
                    {normalizedUser.kyc.document_submitted === "NZ_DRIVER_LICENCE" && (
                      <Col span={12}>
                        <SectionTitle icon={<IdcardOutlined />} title="NZ Driver License & Version" />
                        <InfoItem label="License Number" value={normalizedUser.kyc.document_number ?? "N/A"} />
                        <InfoItem label="Version Number" value={normalizedUser.kyc.document_version ?? "N/A"} />
                        <InfoItem
                          label="View"
                          value={
                            <Button
                              type="link"
                              onClick={() => {
                                setSelectedDocuments(normalizedUser.kyc?.documents || []);
                                setDocumentModal(true);
                              }}
                            >
                              View DL Photo
                            </Button>
                          }
                        />
                      </Col>
                    )}

                    {normalizedUser.kyc.document_submitted === "PASSPORT" && (
                      <Col span={12}>
                        <SectionTitle icon={<IdcardOutlined />} title="Passport" />
                        <InfoItem label="Passport Number" value={normalizedUser.kyc.document_number ?? "N/A"} />
                        <InfoItem label="Expiry Date" value={normalizedUser.kyc.expiry_date ? moment(normalizedUser.kyc.expiry_date).format("DD MMM YYYY") : "N/A"} />
                        <InfoItem
                          label="View"
                          value={
                            <Button
                              type="link"
                              onClick={() => {
                                setSelectedDocuments(normalizedUser.kyc?.documents || []);
                                setDocumentModal(true);
                              }}
                            >
                              View Passport Photo
                            </Button>
                          }
                        />
                      </Col>
                    )}
                  </Row>
                </SurfaceCard>
              </Col>
            )}

            {/* MANUAL SUBSCRIPTION ASSIGNMENT SECTION */}
            <Col span={24}>
              <SurfaceCard
                bg="linear-gradient(135deg, #f8fbff 0%, #f4f8fd 100%)"
                border="1px solid #e3edf7"
              >
                {/* HEADER */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 16,
                    marginBottom: 24,
                  }}
                >
                  <div>
                    <Title
                      level={4}
                      style={{
                        margin: 0,
                        fontWeight: 700,
                        color: "#0f172a",
                      }}
                    >
                      User Subscription Details
                    </Title>

                    <Text
                      style={{
                        color: "#64748b",
                        fontSize: 14,
                      }}
                    >
                      Subscription plan, transaction limits and trial configurations
                    </Text>
                  </div>

                  <Space>
                    {canAssignSubscription && (
                      <Button
                        type="primary"
                        size="large"
                        style={{
                          borderRadius: 12,
                          height: 42,
                          fontWeight: 600,
                          paddingInline: 22,
                          boxShadow: "0 8px 18px rgba(22,119,255,.15)",
                        }}
                        onClick={() => {
                          setAssignModalVisible(true);
                          fetchActivePackages();
                        }}
                      >
                        Assign Subscription Plan
                      </Button>
                    )}
                    {canAssignSubscription && (
                      <Button
                        type="default"
                        size="large"
                        style={{
                          borderRadius: 12,
                          height: 42,
                          fontWeight: 600,
                          paddingInline: 22,
                        }}
                        onClick={() => {
                          const sub = normalizedUser?.subscription || {};
                          setEditDailyLimit(sub.daily_transaction_limit || 0);
                          setEditMonthlyLimit(sub.monthly_transaction_limit || 0);
                          setEditDailyCount(sub.daily_transaction_count || 0);
                          setEditLimitsVisible(true);
                        }}
                      >
                        Edit Limits
                      </Button>
                    )}
                  </Space>
                </div>

                {/* ACTIVE PLAN */}
                <div
                  style={{
                    background: "#ffffff",
                    borderRadius: 18,
                    border: "1px solid #edf2f7",
                    padding: 24,
                    marginBottom: 22,
                  }}
                >
                  <Row gutter={[24, 24]} align="middle">
                    <Col xs={24} lg={16}>
                      <Text
                        style={{
                          fontSize: 14,
                          fontWeight: 700,
                          color: "#111827",
                          display: "block",
                          marginBottom: 10,
                        }}
                      >
                        Active Subscription Plan
                      </Text>

                      <Text
                        style={{
                          fontSize: 22,
                          fontWeight: 500,
                          color: "#475569",
                          display: "block",
                          marginBottom: 18,
                        }}
                      >
                        {normalizedUser?.subscription?.name ||
                          normalizedUser?.subscription?.package_name ||
                          normalizedUser?.subscription?.subscription_package?.name ||
                          "No Active Plan"}
                      </Text>

                      <Space wrap size={[10, 10]}>
                        <Tag
                          color="success"
                          style={{
                            borderRadius: 999,
                            padding: "4px 14px",
                            fontWeight: 600,
                          }}
                        >
                          Active
                        </Tag>

                        {normalizedUser?.subscription?.is_monthly_trial && (
                          <Tag
                            color="processing"
                            style={{
                              borderRadius: 999,
                              padding: "4px 14px",
                              fontWeight: 600,
                            }}
                          >
                            Monthly Trial Enabled
                          </Tag>
                        )}

                        {normalizedUser?.subscription?.is_transaction_trial && (
                          <Tag
                            color="purple"
                            style={{
                              borderRadius: 999,
                              padding: "4px 14px",
                              fontWeight: 600,
                            }}
                          >
                            Transaction Trial Enabled
                          </Tag>
                        )}
                      </Space>
                    </Col>

                    <Col xs={24} lg={8}>
                      <div
                        style={{
                          background: "#f8fafc",
                          borderRadius: 16,
                          padding: 18,
                          border: "1px solid #e5e7eb",
                        }}
                      >
                        <Text
                          style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: "#111827",
                            display: "block",
                            marginBottom: 8,
                          }}
                        >
                          Subscription Status
                        </Text>

                        <Text
                          style={{
                            fontSize: 16,
                            fontWeight: 500,
                            color: "#475569",
                          }}
                        >
                          Active & Operational
                        </Text>
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* LIMITS */}
                <Row gutter={[18, 18]}>
                  {[
                    {
                      title: "Daily Transaction Limit",
                      value:
                        normalizedUser?.subscription?.daily_transaction_limit ?? "0",
                      icon: (
                        <DollarSign
                          style={{ width: 18, height: 18, color: "#14b8a6" }}
                        />
                      ),
                    },
                    {
                      title: "Daily Transaction Count",
                      value:
                        normalizedUser?.subscription?.daily_transaction_count ?? "0",
                      icon: (
                        <RotateCcwSquare
                          style={{ width: 18, height: 18, color: "#7c3aed" }}
                        />
                      ),
                    },
                    {
                      title: "Monthly Transaction Limit",
                      value:
                        normalizedUser?.subscription?.monthly_transaction_limit ?? "0",
                      icon: (
                        <Infinity
                          style={{ width: 18, height: 18, color: "#f59e0b" }}
                        />
                      ),
                    },
                  ].map((item, index) => (
                    <Col xs={24} md={8} key={index}>
                      <div
                        style={{
                          background: "#ffffff",
                          border: "1px solid #edf2f7",
                          borderRadius: 18,
                          padding: 22,
                          height: "100%",
                        }}
                      >
                        <div
                          style={{
                            width: 44,
                            height: 44,
                            borderRadius: 12,
                            background: "#f8fafc",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginBottom: 16,
                            border: "1px solid #eef2f7",
                          }}
                        >
                          {item.icon}
                        </div>

                        <Text
                          style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: "#111827",
                            display: "block",
                            marginBottom: 8,
                          }}
                        >
                          {item.title}
                        </Text>

                        <Text
                          style={{
                            fontSize: 18,
                            fontWeight: 500,
                            color: "#475569",
                          }}
                        >
                          {item.value}
                        </Text>
                      </div>
                    </Col>
                  ))}
                </Row>

                {/* TRIAL SECTION */}
                <Row gutter={[18, 18]} style={{ marginTop: 18 }}>
                  <Col xs={24} md={12}>
                    <div
                      style={{
                        background: "#ffffff",
                        border: "1px solid #edf2f7",
                        borderRadius: 18,
                        padding: 22,
                        height: "100%",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 14,
                        }}
                      >
                        <Text
                          style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: "#111827",
                          }}
                        >
                          Monthly Fee Trial
                        </Text>

                        <Tag
                          color={
                            normalizedUser?.subscription?.is_monthly_trial
                              ? "processing"
                              : "default"
                          }
                          style={{
                            borderRadius: 999,
                            padding: "3px 12px",
                            fontWeight: 600,
                          }}
                        >
                          {normalizedUser?.subscription?.is_monthly_trial
                            ? "Enabled"
                            : "Disabled"}
                        </Tag>
                      </div>

                      <Text
                        style={{
                          fontSize: 18,
                          fontWeight: 500,
                          color: "#475569",
                          display: "block",
                          marginBottom: 8,
                        }}
                      >
                        {normalizedUser?.subscription?.is_monthly_trial
                          ? `${normalizedUser?.subscription?.monthly_trial_days} Days`
                          : "No Trial Available"}
                      </Text>

                      <Text
                        style={{
                          color: "#94a3b8",
                          fontSize: 13,
                          lineHeight: 1.6,
                        }}
                      >
                        Monthly subscription fee waiver configuration.
                      </Text>
                    </div>
                  </Col>

                  <Col xs={24} md={12}>
                    <div
                      style={{
                        background: "#ffffff",
                        border: "1px solid #edf2f7",
                        borderRadius: 18,
                        padding: 22,
                        height: "100%",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 14,
                        }}
                      >
                        <Text
                          style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: "#111827",
                          }}
                        >
                          Transaction Fee Trial
                        </Text>

                        <Tag
                          color={
                            normalizedUser?.subscription?.is_transaction_trial
                              ? "purple"
                              : "default"
                          }
                          style={{
                            borderRadius: 999,
                            padding: "3px 12px",
                            fontWeight: 600,
                          }}
                        >
                          {normalizedUser?.subscription?.is_transaction_trial
                            ? "Enabled"
                            : "Disabled"}
                        </Tag>
                      </div>

                      <Text
                        style={{
                          fontSize: 18,
                          fontWeight: 500,
                          color: "#475569",
                          display: "block",
                          marginBottom: 8,
                        }}
                      >
                        {normalizedUser?.subscription?.is_transaction_trial
                          ? `${normalizedUser?.subscription?.transaction_trial_days} Days`
                          : "No Trial Available"}
                      </Text>

                      <Text
                        style={{
                          color: "#94a3b8",
                          fontSize: 13,
                          lineHeight: 1.6,
                        }}
                      >
                        Transaction fee waiver configuration.
                      </Text>
                    </div>
                  </Col>
                </Row>
              </SurfaceCard>
            </Col>

            {walletRows.length > 0 && (
              <Col span={24}>
                <SurfaceCard bg="linear-gradient(135deg, #f0f9ff, #e6fffb)" border="1px solid #cceeff">
                  <SectionTitle icon={<CreditCardOutlined />} title="Wallet Overview" />
                  <Divider />

                  <Table
                    scroll={{ x: 420 }}
                    dataSource={walletRows}
                    columns={walletColumns}
                    pagination={false}
                    size="small"
                  />
                </SurfaceCard>
              </Col>
            )}

            {normalizedUser?.bank && normalizedUser?.bank.length > 0 && (
              <Col span={24}>
                <SurfaceCard bg="linear-gradient(135deg, #f0f9ff, #e6fffb)" border="1px solid #cceeff">
                  <SectionTitle icon={<CreditCardOutlined />} title="Linked Bank Accounts" />
                  <Divider />
                  
                  <Table
                    scroll={{ x: 420 }}
                    dataSource={normalizedUser.bank}
                    columns={financialBankColumns}
                    pagination={false}
                    size="small"
                  />
                </SurfaceCard>
              </Col>
            )}
          </Row>
        </div>

        {/* ===== FOOTER ===== */}
        <div
          style={{
            padding: 20,
            background: "#ffffff",
            borderTop: "1px solid #eaeaea",
            textAlign: "right",
          }}
        >
          <Button size="large" onClick={onClose}>
            Close
          </Button>
        </div>
      </Modal>

      {/* DOCUMENT MODAL */}
      <Modal
        open={documentModal}
        onCancel={() => setDocumentModal(false)}
        footer={null}
        width={700}
        title="Submitted Document"
        zIndex={1100}
        destroyOnClose
      >
        <Table
          columns={kycDocumentColumns}
          dataSource={selectedDocuments}
          rowKey={(record, index) => index}
          pagination={false}
        />
      </Modal>

      {/* MANUAL SUBSCRIPTION ASSIGNMENT MODAL */}
      <Modal
        open={assignModalVisible}
        onCancel={() => setAssignModalVisible(false)}
        title="Manually Assign Subscription Plan"
        zIndex={1100}
        destroyOnClose
        onOk={handleAssignSubscription}
        confirmLoading={assignLoading}
        okText="Assign Plan"
      >
        <div style={{ padding: "12px 0" }}>
          <Text style={{ display: "block", marginBottom: 12 }}>
            Choose from the list of active user subscription packages to manually assign to this user. No standard payment invoice or ledger transaction will be generated.
          </Text>
          <Select
            placeholder="Select active package"
            style={{ width: "100%", marginBottom: 16 }}
            value={selectedPackageId || undefined}
            onChange={handlePackageChange}
            options={packages.map(pkg => ({
              label: `${pkg.name} ($${pkg.price} / ${pkg.duration_days} days)`,
              value: pkg.id
            }))}
            getPopupContainer={(trigger) => trigger.parentNode}
          />
          
          {selectedPackageId && (
            <Card size="small" style={{ borderRadius: 12, background: "#fafafa" }}>
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <Text strong>Monthly Fee Free Trial</Text>
                  <Switch checked={isMonthlyTrial} onChange={(v) => setIsMonthlyTrial(v)} />
                </div>
                {isMonthlyTrial && (
                  <div>
                    <Text type="secondary">Duration (Days)</Text>
                    <Input type="number" value={monthlyTrialDays} onChange={(e) => setMonthlyTrialDays(e.target.value)} />
                  </div>
                )}
              </div>
              <Divider style={{ margin: "12px 0" }} />
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <Text strong>Transaction Fee Free Trial</Text>
                  <Switch checked={isTransactionTrial} onChange={(v) => setIsTransactionTrial(v)} />
                </div>
                {isTransactionTrial && (
                  <div>
                    <Text type="secondary">Duration (Days)</Text>
                    <Input type="number" value={transactionTrialDays} onChange={(e) => setTransactionTrialDays(e.target.value)} />
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>
      </Modal>

      <Modal
        open={editLimitsVisible}
        onCancel={() => setEditLimitsVisible(false)}
        title="Edit Subscription Limits"
        zIndex={1100}
        destroyOnClose
        onOk={handleUpdateLimits}
        confirmLoading={editLimitsLoading}
        okText="Update Limits"
      >
        <div style={{ padding: "12px 0" }}>
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">Daily Transaction Limit (Amount)</Text>
            <Input type="number" value={editDailyLimit} onChange={(e) => setEditDailyLimit(e.target.value)} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">Monthly Transaction Limit (Amount)</Text>
            <Input type="number" value={editMonthlyLimit} onChange={(e) => setEditMonthlyLimit(e.target.value)} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">Daily Transaction Count</Text>
            <Input type="number" value={editDailyCount} onChange={(e) => setEditDailyCount(e.target.value)} />
          </div>
        </div>
      </Modal>

      <QrDownloadModal
        open={qrModalVisible}
        visible={qrModalVisible}
        onClose={() => setQrModalVisible(false)}
        qrCodeUrl={selectedWalletForQr?.qrCode}
        user={user}
        payId={selectedWalletForQr?.payId}
        zIndex={2000}
        downloadPng={(size) => downloadQrAsPng({ qrCodeUrl: selectedWalletForQr?.qrCode, user, payId: selectedWalletForQr?.payId, size })}
        downloadPdf={(size) => downloadQrAsPdf({ qrCodeUrl: selectedWalletForQr?.qrCode, user, payId: selectedWalletForQr?.payId, size })}
      />
    </>
  );
};

export default UserProfileModal;
